import React, {Component} from 'react';

import {autobind} from 'core-decorators';
import {motion} from 'framer-motion';
import {Parser} from 'html-to-react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {Collapse, Expand} from 'Frontend/assets/images/icons';
import {FONTS} from 'Frontend/utils/fonts';
import {clampCss, iconAsBackground, pxToVw} from 'Frontend/utils/helpers';
import {MEDIA} from 'Frontend/utils/mediaQueries';
import {isValidNode, processingInstructions} from 'Frontend/utils/parserInstructions';

const AccordionAnim = {
    closed: {height: '0'},
    initial: {height: '0'},
    open: {height: 'auto'}
};

/**
 * AccordionUi
 *
 * @property {*} prop Prop comment.
 *
 * @class AccordionUi
 * @extends {Component}
 */
class AccordionUi extends Component {
    static propTypes = {
        accordionName: PropTypes.string.isRequired,
        accordionBody: PropTypes.string,
        accordionBodyComponent: PropTypes.node
    }

    static defaultProps = {
        accordionBody: null,
        accordionBodyComponent: null
    }

    htmlParser = new Parser();

    /**
     * Creates an instance of AccordionUi.
     *
     * @param {Object} props Component props.
     * @memberof AccordionUi
     */
    constructor(props) {
        super(props);

        this.state = {open: false};
    }

    /**
     * Toggles the accordion open or closed.
     *
     * @memberof AccordionUi
     */
    @autobind
    handleAccordionToggle() {
        this.setState(oldState => ({open: !oldState.open}));
    }

    /**
     * Renders the Component.
     *
     * @returns {JSX} Component.
     * @memberof AccordionUi
     */
    render() {
        const {open} = this.state;
        const {accordionBody, accordionBodyComponent, accordionName} = this.props;

        return (
            <AccordionUiElement>
                <AccordionUiHead open={open} onClick={this.handleAccordionToggle}>{accordionName}</AccordionUiHead>
                <AccordionUiBody
                    animate={open ? 'open' : 'closed'}
                    initial="initial"
                    variants={AccordionAnim}
                >
                    {accordionBodyComponent && accordionBodyComponent}
                    {(accordionBody && !accordionBodyComponent) && this.htmlParser.parseWithInstructions(
                        `<div data-module="AccordionUi">${accordionBody}</div>`,
                        isValidNode,
                        processingInstructions
                    )}
                </AccordionUiBody>
            </AccordionUiElement>
        );
    }
}

export default AccordionUi;

const AccordionUiElement = styled.div`
    border-bottom: 1px solid ${({theme}) => theme.colors.darkBorder};

    &:first-of-type {
        margin-top: ${clampCss(15.4, 'm')};
    }

    ${MEDIA.TABLET.UP} {
        &:first-of-type {
            margin-top: ${clampCss(15.4, 't')};
        }
    }

    ${MEDIA.DESKTOP.UP} {
        &:first-of-type {
            margin-top: max(7px, ${pxToVw(7, 'd')});
        }
    }
`;

const AccordionUiHead = styled.h4`
    ${FONTS.REGULAR}
    color: ${({theme}) => theme.colors.textColor};
    cursor: pointer;
    font-size: ${clampCss(16, 'm')};
    letter-spacing: ${clampCss(1.2, 'm')};
    line-height: ${clampCss(22, 'm')};
    margin: 0;
    padding: ${clampCss(17, 'm')} ${clampCss(17, 'm')} ${clampCss(17, 'm')} ${clampCss(44, 'm')};
    position: relative;

    &::before {
        background-image: ${({open}) => (open ? `url(${iconAsBackground(Collapse)})` : `url(${iconAsBackground(Expand)})`)};
        background-position: center center;
        background-repeat: no-repeat;
        background-size: contain;
        content: '';
        height: ${clampCss(16, 'm')};
        left: ${clampCss(7, 'm')};
        position: absolute;
        top: 50%;
        transform: translate(0, -50%);
        width: ${clampCss(16, 'm')};
    }

    ${MEDIA.TABLET.UP} {
        font-size: ${clampCss(16, 't')};
        letter-spacing: ${clampCss(1.2, 't')};
        line-height: ${clampCss(22, 't')};
        padding: ${clampCss(17, 't')} ${clampCss(17, 't')} ${clampCss(17, 't')} ${clampCss(44, 't')};

        &::before {
            height: ${clampCss(16, 't')};
            left: ${clampCss(7, 't')};
            width: ${clampCss(16, 't')};
        }
    }

    ${MEDIA.DESKTOP.UP} {
        font-size: max(16px, ${pxToVw(16, 'd')});
        letter-spacing: max(1.2px, ${pxToVw(1.2, 'd')});
        line-height: max(22px, ${pxToVw(22, 'd')});
        padding: max(17px, ${pxToVw(17, 'd')}) max(17px, ${pxToVw(17, 'd')}) max(17px, ${pxToVw(17, 'd')}) max(44px, ${pxToVw(44, 'd')});

        &::before {
            height: max(16px, ${pxToVw(16, 'd')});
            left: max(7px, ${pxToVw(7, 'd')});
            width: max(16px, ${pxToVw(16, 'd')});
        }
    }
`;

const AccordionUiBody = styled(motion.div)`
    ${FONTS.REGULAR}
    color: ${({theme}) => theme.colors.textColor};
    font-size: ${clampCss(12, 'm')};
    height: 0;
    letter-spacing: ${clampCss(0.5, 'm')};
    line-height: ${clampCss(16.8, 'm')};
    overflow: hidden;
    padding-left: ${clampCss(44, 'm')};
    padding-right: ${clampCss(44, 'm')};
    text-align: ${({position}) => position};

    ${MEDIA.TABLET.UP} {
        padding-left: ${clampCss(44, 't')};
        padding-right: ${clampCss(44, 't')};
        font-size: ${clampCss(12, 't')};
        letter-spacing: ${clampCss(0.5, 't')};
        line-height: ${clampCss(16.8, 't')};
    }

    ${MEDIA.DESKTOP.UP} {
        font-size: max(14px, ${pxToVw(14, 'd')});
        letter-spacing: max(0.5px, ${pxToVw(0.5, 'd')});
        line-height: max(19.6px, ${pxToVw(19.6, 'd')});
        padding-left: 0;
        padding-right: 0;
    }
`;